import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useDesign } from '../../contexts/DesignContext';
import { useLoading } from '../../contexts/LoadingContext';
import { 
  ShoppingCartIcon, 
  UserIcon, 
  Bars3Icon, 
  XMarkIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoryImageErrors, setCategoryImageErrors] = useState({});
  const [openDropdown, setOpenDropdown] = useState(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const categoryRefs = useRef({});
  const userDropdownRef = useRef(null);
  const desktopUserDropdownRef = useRef(null);
  const mobileUserDropdownRef = useRef(null);
  const { user, logout } = useAuth();
  const { cartItemsCount } = useCart();
  const { designConfig } = useDesign();
  const { track } = useLoading();
  const navigate = useNavigate();
  const location = useLocation();

  // Close any open dropdown/menu on navigation - without this, a category flyout or the
  // account menu opened on one page stays "open" in state and renders on top of the next page,
  // since Header persists across route changes instead of remounting.
  useEffect(() => {
    setOpenDropdown(null);
    setUserDropdownOpen(false);
    setIsMenuOpen(false);
  }, [location.pathname, location.search]);

  const bagQuickLinks = [
    { label: 'All Bags', url: '/products?search=bag' },
    { label: 'School Bags', url: '/products?search=school%20bag' },
    { label: 'Ladies Handbags', url: '/products?search=ladies%20handbag' },
    { label: 'Wallets', url: '/products?search=wallet' },
    { label: 'Gents Handbags', url: '/products?search=gents%20handbag' },
    { label: 'Model Bags', url: '/products?search=model%20bag' }
  ];

  // Inline user dropdown menu
  const renderUserDropdownMenu = () => userDropdownOpen ? (
    <div
      className="user-account-dropdown absolute right-0 mt-1 w-48 bg-white rounded-md shadow-2xl border border-gray-200 py-1"
      onMouseDown={(e) => e.stopPropagation()}
      style={{ zIndex: 99999, top: '100%' }}
    >
      <a
        href="/profile"
        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
        onClick={() => setUserDropdownOpen(false)}
      >
        Profile
      </a>
      <a
        href="/addresses"
        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
        onClick={() => setUserDropdownOpen(false)}
      >
        My Addresses
      </a>
      <a
        href="/orders"
        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
        onClick={() => setUserDropdownOpen(false)}
      >
        My Orders
      </a>
      {user && user.role === 'admin' && (
        <>
          <a
            href="/dashboard/products"
            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            onClick={() => setUserDropdownOpen(false)}
          >
            Manage Products
          </a>
          <a
            href="/dashboard/categories"
            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            onClick={() => setUserDropdownOpen(false)}
          >
            Manage Categories
          </a>
          <a
            href="/dashboard/settings"
            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            onClick={() => setUserDropdownOpen(false)}
          >
            System Settings
          </a>
        </>
      )}
      <button
        onClick={() => { handleLogout(); setUserDropdownOpen(false); }}
        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
      >
        Logout
      </button>
    </div>
  ) : null;

  const getAssetUrl = (assetPath) => {
    if (!assetPath) return '';
    if (/^https?:\/\//i.test(assetPath)) return assetPath;

    const API_BASE = process.env.REACT_APP_API_URL || '';
    const normalizedPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`;
    return `${API_BASE}${normalizedPath}`;
  };

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideLegacyDesktop = userDropdownRef.current && userDropdownRef.current.contains(event.target);
      const clickedInsideDesktop = desktopUserDropdownRef.current && desktopUserDropdownRef.current.contains(event.target);
      const clickedInsideMobile = mobileUserDropdownRef.current && mobileUserDropdownRef.current.contains(event.target);
      const clickedInsideMenu = event.target && event.target.closest && event.target.closest('.user-account-dropdown');
      if (!clickedInsideLegacyDesktop && !clickedInsideDesktop && !clickedInsideMobile && !clickedInsideMenu) {
        setUserDropdownOpen(false);
      }
    };

    if (userDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [userDropdownOpen]);

  const fetchCategories = useCallback(async () => {
    try {
      await track(async () => {
        const response = await fetch('/api/categories');
        const data = await response.json();
        if (data.success) {
          setCategories(data.data.categories || []);
        }
      });
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, [track]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Helper function to handle dropdown opening with timeout management
  const handleDropdownEnter = (dropdownKey) => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setOpenDropdown(dropdownKey);
    // Calculate position for fixed dropdown
    if (categoryRefs.current[dropdownKey]) {
      const rect = categoryRefs.current[dropdownKey].getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX
      });
    }
  };

  // Helper function to handle dropdown closing with delay
  const handleDropdownLeave = (dropdownKey, fallbackKey = null) => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    const timeout = setTimeout(() => {
      setOpenDropdown(fallbackKey);
      setHoverTimeout(null);
    }, 200);
    setHoverTimeout(timeout);
  };

  // Recursive component to render nested categories at any depth
  const renderNestedCategory = (category, parentPath = '', level = 0) => {
    const hasSubcategories = category.subcategories && category.subcategories.length > 0;
    const dropdownKey = parentPath ? `${parentPath}-${category.id}` : `${category.id}`;
    const isOpen = openDropdown === dropdownKey;

    if (hasSubcategories) {
      return (
        <div
          key={category.id}
          className="relative group/sub"
          onMouseEnter={() => handleDropdownEnter(dropdownKey)}
          onMouseLeave={() => handleDropdownLeave(dropdownKey, parentPath || null)}
        >
          {/* Make the parent category clickable too */}
          <Link
            to={`/products?subcategory=${category.id}`}
            className="flex items-center justify-between px-4 py-2 text-sm transition duration-200 hover:bg-gray-50 cursor-pointer"
            style={{ color: designConfig.text_color, pointerEvents: 'auto' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = designConfig.primary_color;
              e.currentTarget.style.backgroundColor = designConfig.background_color || '#FFF8F5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = designConfig.text_color;
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (hoverTimeout) {
                clearTimeout(hoverTimeout);
                setHoverTimeout(null);
              }
              setOpenDropdown(null);
            }}
          >
            <span className="flex items-center gap-2 min-w-0">
              {category.image && !categoryImageErrors[category.id] ? (
                <img
                  src={getAssetUrl(category.image)}
                  alt={category.name}
                  className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                  loading="lazy"
                  onError={() => {
                    setCategoryImageErrors((prev) => ({
                      ...prev,
                      [category.id]: true
                    }));
                  }}
                />
              ) : (
                <span
                  className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold border border-gray-200 flex-shrink-0"
                  style={{ color: designConfig.primary_color, backgroundColor: '#FFFFFF' }}
                >
                  {(category.name || '?').trim().charAt(0).toUpperCase()}
                </span>
              )}
              <span className="truncate">{category.name}</span>
            </span>
            <ChevronDownIcon 
              className="w-3 h-3 rotate-[-90deg]" 
              style={{ color: designConfig.text_color }} 
            />
          </Link>
          
          {/* Nested Dropdown - Recursive */}
          {isOpen && (
            <div 
              className="absolute left-full top-0 ml-1 bg-white rounded-lg shadow-2xl border border-gray-200 py-2"
              style={{ 
                minWidth: '180px', 
                maxWidth: '90vw', 
                pointerEvents: 'auto',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                zIndex: 99999
              }}
              onMouseEnter={() => handleDropdownEnter(dropdownKey)}
              onMouseLeave={() => handleDropdownLeave(dropdownKey, parentPath || null)}
            >
              {category.subcategories.map((subCat) => 
                renderNestedCategory(subCat, dropdownKey, level + 1)
              )}
            </div>
          )}
        </div>
      );
    } else {
      return (
        <Link
          key={category.id}
          to={`/products?subcategory=${category.id}`}
          className="flex items-center gap-2 px-4 py-2 text-sm transition duration-200 hover:bg-gray-50 cursor-pointer"
          style={{ color: designConfig.text_color, pointerEvents: 'auto' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = designConfig.primary_color;
            e.currentTarget.style.backgroundColor = designConfig.background_color || '#FFF8F5';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = designConfig.text_color;
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (hoverTimeout) {
              clearTimeout(hoverTimeout);
              setHoverTimeout(null);
            }
            setOpenDropdown(null);
          }}
        >
          {category.image && !categoryImageErrors[category.id] ? (
            <img
              src={getAssetUrl(category.image)}
              alt={category.name}
              className="w-5 h-5 rounded-full object-cover flex-shrink-0"
              loading="lazy"
              onError={() => {
                setCategoryImageErrors((prev) => ({
                  ...prev,
                  [category.id]: true
                }));
              }}
            />
          ) : (
            <span
              className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold border border-gray-200 flex-shrink-0"
              style={{ color: designConfig.primary_color, backgroundColor: '#FFFFFF' }}
            >
              {(category.name || '?').trim().charAt(0).toUpperCase()}
            </span>
          )}
          <span className="truncate">{category.name}</span>
        </Link>
      );
    }
  };

  const executeSearch = () => {
    const trimmed = searchQuery.trim();
    if (!trimmed) return;

    const target = `/products?search=${encodeURIComponent(trimmed)}`;
    try {
      navigate(target);
    } catch (error) {
      window.location.href = target;
    }
    setSearchQuery('');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    executeSearch();
  };

  const handleLogout = () => {
    const isDashboardRoute = window.location.pathname.startsWith('/dashboard');
    logout(isDashboardRoute); // Pass true for dashboard logout, false for e-commerce logout
    // If we're on a dashboard route, redirect to dashboard login, otherwise to home
    if (isDashboardRoute) {
      navigate('/dashboard/login');
    } else {
      navigate('/');
    }
  };

  // Recursive component for mobile nested categories
  const renderMobileNestedCategory = (category, parentPath = '', level = 0) => {
    const hasSubcategories = category.subcategories && category.subcategories.length > 0;
    const dropdownKey = parentPath ? `${parentPath}-${category.id}` : `${category.id}`;
    const isOpen = openDropdown === dropdownKey;

    if (hasSubcategories) {
      return (
        <div key={category.id}>
          <button
            onClick={() => setOpenDropdown(isOpen ? parentPath : dropdownKey)}
            className="flex items-center justify-between w-full py-2 text-sm transition duration-200"
            style={{ 
              color: designConfig.text_color, 
              opacity: Math.max(0.8 - (level * 0.1), 0.6),
              paddingLeft: `${level * 16}px`
            }}
            onMouseEnter={(e) => e.target.style.color = designConfig.primary_color}
            onMouseLeave={(e) => {
              e.target.style.color = designConfig.text_color;
            }}
          >
            <span className="flex items-center gap-2 min-w-0">
              {category.image && !categoryImageErrors[category.id] ? (
                <img
                  src={getAssetUrl(category.image)}
                  alt={category.name}
                  className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                  loading="lazy"
                  onError={() => {
                    setCategoryImageErrors((prev) => ({
                      ...prev,
                      [category.id]: true
                    }));
                  }}
                />
              ) : (
                <span
                  className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold border border-gray-200 flex-shrink-0"
                  style={{ color: designConfig.primary_color, backgroundColor: '#FFFFFF' }}
                >
                  {(category.name || '?').trim().charAt(0).toUpperCase()}
                </span>
              )}
              <span className="truncate">{category.name}</span>
            </span>
            <ChevronDownIcon 
              className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
            />
          </button>
          
          {isOpen && (
            <div className="space-y-1">
              {category.subcategories.map((subCat) => 
                renderMobileNestedCategory(subCat, dropdownKey, level + 1)
              )}
            </div>
          )}
        </div>
      );
    } else {
      return (
        <Link
          key={category.id}
          to={`/products?subcategory=${category.id}`}
          className="flex items-center gap-2 py-2 text-sm transition duration-200"
          style={{ 
            color: designConfig.text_color, 
            opacity: Math.max(0.8 - (level * 0.1), 0.6),
            paddingLeft: `${level * 16}px`
          }}
          onMouseEnter={(e) => e.target.style.color = designConfig.primary_color}
          onMouseLeave={(e) => {
            e.target.style.color = designConfig.text_color;
          }}
          onClick={() => {
            setIsMenuOpen(false);
            setOpenDropdown(null);
          }}
        >
          {category.image && !categoryImageErrors[category.id] ? (
            <img
              src={getAssetUrl(category.image)}
              alt={category.name}
              className="w-5 h-5 rounded-full object-cover flex-shrink-0"
              loading="lazy"
              onError={() => {
                setCategoryImageErrors((prev) => ({
                  ...prev,
                  [category.id]: true
                }));
              }}
            />
          ) : (
            <span
              className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold border border-gray-200 flex-shrink-0"
              style={{ color: designConfig.primary_color, backgroundColor: '#FFFFFF' }}
            >
              {(category.name || '?').trim().charAt(0).toUpperCase()}
            </span>
          )}
          <span className="truncate">{category.name}</span>
        </Link>
      );
    }
  };

  return (
    <>
      <header 
        className="shadow-md border-b border-gray-100 sticky top-0 bg-white"
        style={{ 
          backgroundColor: designConfig.header_bg || '#FFFFFF',
          zIndex: 10000,
          position: 'sticky',
          
        }}
      >
      <div className="container mx-auto px-2 sm:px-4 max-w-full">
        {/* ── Row 1: Logo + Search + Account/Cart ── */}
        <div className="flex items-center justify-between h-16 sm:h-20 gap-2 min-w-0">
          {/* Logo - Elegant Style */}
          <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group min-w-0 flex-1 md:flex-none">
            {designConfig.logo_image ? (
              // Use uploaded logo image
              <div className="flex-shrink-0 transition-transform duration-300 group-hover:scale-110">
                <img
                  src={designConfig.logo_image}
                  alt={designConfig.site_name || 'Logo'}
                  className="h-8 w-auto sm:h-10 md:h-12 max-w-[120px] sm:max-w-[150px] md:max-w-[200px] object-contain"
                  style={{ maxWidth: '100%', height: 'auto' }}
                />
              </div>
            ) : (
              // Fallback to logo text in circular badge
              <div 
                className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110 flex-shrink-0"
                style={{ 
                  background: `linear-gradient(135deg, ${designConfig.primary_color}, ${designConfig.accent_color})`
                }}
              >
                <span className="text-white font-light text-base sm:text-lg md:text-xl tracking-wide" style={{ fontFamily: 'serif' }}>
                  {designConfig.logo_text || 'V'}
                </span>
              </div>
            )}
            <div className="flex flex-col min-w-0 flex-1 sm:flex-none">
              <span 
                className="text-[15px] sm:text-base md:text-lg lg:text-xl font-light tracking-wide truncate"
                style={{ 
                  color: designConfig.text_color,
                  fontFamily: 'serif'
                }}
              >
                {designConfig.site_name || 'Viv Fashion Boutique'}
              </span>
              {designConfig.site_tagline && (
                <>
                  <span 
                    className="hidden sm:block text-[10px] md:text-xs tracking-widest uppercase truncate mt-0.5"
                    style={{ 
                      color: designConfig.text_color,
                      opacity: 0.6
                    }}
                  >
                    {designConfig.site_tagline}
                  </span>
                  {/* Decorative line after tagline */}
                  <div 
                    className="hidden sm:block h-px mt-1"
                    style={{ 
                      backgroundColor: designConfig.text_color,
                      opacity: 0.2,
                      width: '100%',
                      maxWidth: '120px',
                      minHeight: '1px'
                    }}
                  ></div>
                </>
              )}
            </div>
          </Link>

          {/* Search Bar - Elegant Style */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-md lg:max-w-lg mx-2 lg:mx-8 min-w-0">
            <div className="relative w-full">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search elegant fashion, bags, toys..."
                className="w-full pl-5 pr-12 py-3 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-300"
                style={{ 
                  focusRingColor: designConfig.primary_color,
                  backgroundColor: '#FFF8F5'
                }}
                onFocus={(e) => e.target.style.borderColor = designConfig.primary_color}
                onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
              />
              <button
                type="submit"
                onClick={executeSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 rounded-full transition-all duration-300 hover:scale-110"
                style={{ color: designConfig.primary_color }}
              >
                <MagnifyingGlassIcon className="w-5 h-5" />
              </button>
            </div>
          </form>

          {/* Desktop: Login / Register / Cart in Row 1 */}
          <div className="hidden md:flex items-center gap-3 flex-shrink-0">
            {user ? (
              <>
                <Link
                  to="/orders"
                  className="transition duration-200 font-medium text-sm"
                  style={{ color: designConfig.text_color }}
                  onMouseEnter={(e) => e.target.style.color = designConfig.primary_color}
                  onMouseLeave={(e) => e.target.style.color = designConfig.text_color}
                >
                  Orders
                </Link>
                <div className="relative" ref={desktopUserDropdownRef} style={{ zIndex: 100000, position: 'relative' }}>
                  <button
                    onClick={() => setUserDropdownOpen((prev) => !prev)}
                    className="flex items-center space-x-1 transition duration-200 px-2 py-1 rounded hover:bg-gray-100 text-sm"
                    style={{ color: designConfig.text_color }}
                  >
                    <UserIcon className="w-5 h-5" />
                    <span>{user.first_name}</span>
                    <ChevronDownIcon className={`w-4 h-4 transition-transform ${userDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {renderUserDropdownMenu()}
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="transition duration-200 font-medium text-sm"
                  style={{ color: designConfig.text_color }}
                  onMouseEnter={(e) => e.target.style.color = designConfig.primary_color}
                  onMouseLeave={(e) => e.target.style.color = designConfig.text_color}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="text-white px-4 py-2 rounded-lg transition duration-200 text-sm font-medium"
                  style={{ backgroundColor: designConfig.primary_color }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.9'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  Register
                </Link>
              </>
            )}
            <Link
              to="/cart"
              className="relative transition duration-200"
              style={{ color: designConfig.text_color }}
              onMouseEnter={(e) => e.target.style.color = designConfig.primary_color}
              onMouseLeave={(e) => e.target.style.color = designConfig.text_color}
            >
              <ShoppingCartIcon className="w-6 h-6" />
              {cartItemsCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cartItemsCount}
                </span>
              )}
            </Link>
          </div>

          {/* Mobile quick actions (login/account + cart) + menu */}
          <div className="md:hidden flex items-center gap-1 flex-shrink-0 pl-1 ml-auto">
            {user ? (
              <button
                ref={mobileUserDropdownRef}
                onClick={() => {
                  setUserDropdownOpen((prev) => !prev);
                }}
                className="flex items-center gap-1 px-1 py-1 rounded-full border border-gray-200 min-w-0"
                style={{ color: designConfig.text_color, backgroundColor: '#FFFFFF' }}
                aria-label="Account"
              >
                <UserIcon className="w-5 h-5" />
                <span className="hidden md:inline text-xs font-medium max-w-[64px] truncate">{user.first_name}</span>
              </button>
            ) : (
              <Link
                to="/login"
                className="px-2.5 py-1.5 rounded-full text-white text-xs font-semibold whitespace-nowrap"
                style={{ backgroundColor: designConfig.primary_color }}
                aria-label="Login"
              >
                Login
              </Link>
            )}

            <Link
              to="/cart"
              className="relative p-1.5 rounded-full border border-gray-200 shrink-0"
              style={{ color: designConfig.text_color, backgroundColor: '#FFFFFF' }}
              aria-label="Cart"
            >
              <ShoppingCartIcon className="w-5 h-5" />
              {cartItemsCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1">
                  {cartItemsCount}
                </span>
              )}
            </Link>

            <button
              type="button"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-1.5 rounded-full border border-gray-200 flex-shrink-0 ml-0.5"
              style={{ color: '#1F2937', backgroundColor: '#FFFFFF' }}
              aria-label="Menu"
            >
              {isMenuOpen ? (
                <XMarkIcon className="w-7 h-7 text-gray-800" style={{ strokeWidth: 2.5 }} />
              ) : (
                <Bars3Icon className="w-7 h-7 text-gray-800" style={{ strokeWidth: 2.5 }} />
              )}
            </button>
            {renderUserDropdownMenu()}
          </div>
        </div>

        {/* ── Row 2: Category Navigation (desktop) ── */}
        {categories.length > 0 && (
          <div className="hidden md:flex items-center gap-1 flex-wrap border-t border-gray-100 py-2">
            <Link
              to="/"
              className="px-3 py-1.5 text-sm font-medium whitespace-nowrap transition duration-200 rounded"
              style={{ color: designConfig.text_color }}
              onMouseEnter={(e) => e.currentTarget.style.color = designConfig.primary_color}
              onMouseLeave={(e) => e.currentTarget.style.color = designConfig.text_color}
            >
              Home
            </Link>
            {categories.map((category) => {
              const hasSubcategories = category.subcategories && category.subcategories.length > 0;
              const dropdownKey = `${category.id}`;
              const isOpen = openDropdown === dropdownKey;
              return (
                <div
                  key={category.id}
                  className="relative"
                  onMouseEnter={() => hasSubcategories && handleDropdownEnter(dropdownKey)}
                  onMouseLeave={() => hasSubcategories && handleDropdownLeave(dropdownKey, null)}
                >
                  <Link
                    to={`/products?category=${category.id}`}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium whitespace-nowrap transition duration-200 rounded"
                    style={{ color: designConfig.text_color }}
                    onMouseEnter={(e) => e.currentTarget.style.color = designConfig.primary_color}
                    onMouseLeave={(e) => e.currentTarget.style.color = designConfig.text_color}
                    onClick={() => setOpenDropdown(null)}
                  >
                    {category.name}
                    {hasSubcategories && (
                      <ChevronDownIcon className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    )}
                  </Link>
                  {hasSubcategories && isOpen && (
                    <div
                      className="absolute left-0 top-full bg-white rounded-lg shadow-2xl border border-gray-200 py-2"
                      style={{ minWidth: '200px', zIndex: 99999, boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)' }}
                      onMouseEnter={() => handleDropdownEnter(dropdownKey)}
                      onMouseLeave={() => handleDropdownLeave(dropdownKey, null)}
                    >
                      {category.subcategories.map((subCat) => renderNestedCategory(subCat, dropdownKey, 1))}
                    </div>
                  )}
                </div>
              );
            })}
            <Link
              to="/products"
              className="px-3 py-1.5 text-sm font-medium whitespace-nowrap transition duration-200 rounded"
              style={{ color: designConfig.text_color }}
              onMouseEnter={(e) => e.currentTarget.style.color = designConfig.primary_color}
              onMouseLeave={(e) => e.currentTarget.style.color = designConfig.text_color}
            >
              All Products
            </Link>
          </div>
        )}

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t">
            {/* Mobile Search */}
            <form onSubmit={handleSearch} className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products..."
                  className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400"
                >
                  <MagnifyingGlassIcon className="w-5 h-5" />
                </button>
              </div>
            </form>

            <div className="space-y-2">
              {/* Home Link */}
              <Link
                to="/"
                className="block py-2 font-medium transition duration-200"
                style={{ color: designConfig.text_color }}
                onMouseEnter={(e) => e.target.style.color = designConfig.primary_color}
                onMouseLeave={(e) => e.target.style.color = designConfig.text_color}
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              
              {/* All Main Categories with Mobile Dropdowns */}
              {categories.map((category) => (
                <div key={category.id}>
                  {category.subcategories && category.subcategories.length > 0 ? (
                    <>
                      <div className="flex items-center justify-between w-full">
                        <Link
                          to={`/products?category=${category.id}`}
                          className="flex-1 py-2 font-medium transition duration-200"
                          style={{ color: designConfig.text_color }}
                          onMouseEnter={(e) => e.target.style.color = designConfig.primary_color}
                          onMouseLeave={(e) => e.target.style.color = designConfig.text_color}
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {category.name}
                        </Link>
                        <button
                          onClick={() => setOpenDropdown(openDropdown === category.id ? null : category.id)}
                          className="p-2 transition duration-200"
                          style={{ color: designConfig.text_color }}
                        >
                          <ChevronDownIcon 
                            className={`w-4 h-4 transition-transform ${openDropdown === category.id ? 'rotate-180' : ''}`} 
                          />
                        </button>
                      </div>
                      
                      {openDropdown === category.id && (
                        <div className="pl-4 space-y-1">
                          {/* Link to show all products in this category */}
                          <Link
                            to={`/products?category=${category.id}`}
                            className="block py-2 text-sm font-semibold transition duration-200"
                            style={{ color: designConfig.primary_color }}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            All {category.name}
                          </Link>
                          {category.subcategories.map((subcategory) => 
                            renderMobileNestedCategory(subcategory, category.id.toString(), 1)
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={`/products?category=${category.id}`}
                      className="block py-2 font-medium transition duration-200"
                      style={{ color: designConfig.text_color }}
                      onMouseEnter={(e) => e.target.style.color = designConfig.primary_color}
                      onMouseLeave={(e) => e.target.style.color = designConfig.text_color}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {category.name}
                    </Link>
                  )}
                </div>
              ))}

              {/* Mobile Bags shortcuts */}
              <div>
                <div className="flex items-center justify-between w-full">
                  <Link
                    to="/products?search=bag"
                    className="flex-1 py-2 font-medium transition duration-200"
                    style={{ color: designConfig.text_color }}
                    onMouseEnter={(e) => e.target.style.color = designConfig.primary_color}
                    onMouseLeave={(e) => e.target.style.color = designConfig.text_color}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Bags
                  </Link>
                  <button
                    onClick={() => setOpenDropdown(openDropdown === 'bags_static_mobile' ? null : 'bags_static_mobile')}
                    className="p-2 transition duration-200"
                    style={{ color: designConfig.text_color }}
                  >
                    <ChevronDownIcon
                      className={`w-4 h-4 transition-transform ${openDropdown === 'bags_static_mobile' ? 'rotate-180' : ''}`}
                    />
                  </button>
                </div>

                {openDropdown === 'bags_static_mobile' && (
                  <div className="pl-4 space-y-1">
                    {bagQuickLinks.map((bagLink) => (
                      <Link
                        key={bagLink.label}
                        to={bagLink.url}
                        className="block py-2 text-sm font-medium transition duration-200"
                        style={{ color: designConfig.text_color }}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {bagLink.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              
              {/* All Products Link */}
              <Link
                to="/products"
                className="block py-2 font-medium transition duration-200"
                style={{ color: designConfig.text_color }}
                onMouseEnter={(e) => e.target.style.color = designConfig.primary_color}
                onMouseLeave={(e) => e.target.style.color = designConfig.text_color}
                onClick={() => setIsMenuOpen(false)}
              >
                All Products
              </Link>
              
              <Link
                to="/cart"
                className="flex items-center justify-between py-2 transition duration-200"
                style={{ color: designConfig.text_color }}
                onMouseEnter={(e) => e.target.style.color = designConfig.primary_color}
                onMouseLeave={(e) => e.target.style.color = designConfig.text_color}
                onClick={() => setIsMenuOpen(false)}
              >
                <span>Cart</span>
                {cartItemsCount > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {cartItemsCount}
                  </span>
                )}
              </Link>

              {user ? (
                <>
                  <Link
                    to="/profile"
                    className="block py-2 text-gray-700 hover:text-blue-600"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Profile
                  </Link>
                  <Link
                    to="/addresses"
                    className="block py-2 text-gray-700 hover:text-blue-600"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    My Addresses
                  </Link>
                  <Link
                    to="/orders"
                    className="block py-2 text-gray-700 hover:text-blue-600"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Orders
                  </Link>
                  {(user.role === 'admin' || user.role === 'employee') && (
                    <>
                      <Link
                        to="/dashboard"
                        className="block py-2 text-gray-700 hover:text-blue-600"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Dashboard
                      </Link>
                    </>
                  )}
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="block w-full text-left py-2 text-gray-700 hover:text-blue-600"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="block py-2 text-gray-700 hover:text-blue-600"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="block py-2 text-white rounded-lg text-center transition duration-200"
                    style={{ backgroundColor: designConfig.primary_color }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Register
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
    
    {/* Promotional Banner - Removed, replaced by AnnouncementBanner */}
    </>
  );
};

export default Header;
